import { useTranslation } from 'react-i18next';
import {
  Accordion,
  Button,
  Checkbox,
  FormRow,
  Input,
  Modal,
  Radio,
  Text,
  TextArea,
  Table
} from '@efast_public/fjd-component-library';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import { createJoiWithTranslations } from '../utils/joiConfig';
import {
  Authentication,
  AuthUpdatePayload,
  GovOSAuthLevel,
  GovOsResult
} from '../api/govos.types';
import {
  GovosApiRoutes,
  govosClient,
  invalidateConfiguredOnlinediensteCache,
  invalidateServiceTemplatesCache
} from '../api/govos-api';
import { useNotification } from '../shared/notification/useNotification';
import { useTemplateNameValidation } from '../hooks/useTemplateNameValidation';

interface EditAuthenticationOptionsModalProps {
  startAppId: string | number;
  processId?: string | number;
  templateId?: string | number;
  authentication?: Authentication;
  onClose: () => void;
  isOpen: boolean;
  isTemplatePage?: boolean;
  templateName?: string;
  existingNames?: string[];
}

interface AuthOptionsFormData {
  loginAsCitizen: boolean;
  loginAsEmployee: boolean;
  authLevel: GovOSAuthLevel;
  shouldUseStandardPrivacyText: boolean;
  customPrivacyText: string;
}

function getDefaultValues(auth?: Authentication): AuthOptionsFormData {
  return {
    loginAsCitizen: auth?.loginAsCitizen ?? false,
    loginAsEmployee: auth?.loginAsEmployee ?? false,
    authLevel: auth?.authLevel ?? 'unverified',
    shouldUseStandardPrivacyText: !auth?.privacyText,
    customPrivacyText: auth?.privacyText ?? ''
  };
}

export function EditAuthenticationOptionsModal({
  startAppId,
  processId,
  templateId,
  authentication,
  onClose,
  isOpen,
  isTemplatePage,
  templateName,
  existingNames
}: EditAuthenticationOptionsModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { notifyUserFromRequest } = useNotification();
  const CustomJoi = createJoiWithTranslations(t);

  const defaultValues = getDefaultValues(authentication);

  const validationSchema = CustomJoi.object<AuthOptionsFormData>({
    loginAsCitizen: CustomJoi.boolean().optional(),
    loginAsEmployee: CustomJoi.boolean().optional(),
    authLevel: CustomJoi.string().required(),
    shouldUseStandardPrivacyText: CustomJoi.boolean().optional(),
    customPrivacyText: CustomJoi.string().allow('').optional()
  });

  const { control, handleSubmit, formState, setValue, watch } =
    useForm<AuthOptionsFormData>({
      defaultValues,
      resolver: joiResolver(validationSchema),
      mode: 'onBlur'
    });

  const authLevel = watch('authLevel');
  const shouldUseStandardPrivacyText = watch('shouldUseStandardPrivacyText');
  const customPrivacyText = watch('customPrivacyText');

  const isTemplateMode = templateId != null;
  const [shouldSaveAsTemplate, setShouldSaveAsTemplate] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [templateNameInModal, setTemplateNameInModal] = useState(
    templateName ?? ''
  );

  const { templateNameError, validateTemplateName, clearTemplateNameError } =
    useTemplateNameValidation({
      currentTemplateName: isTemplatePage ? templateName : undefined,
      existingNames,
      t,
      validationKeyPrefix: 'EditAuthenticationOptionsModal'
    });

  const { mutate: createTemplate } = useMutation({
    mutationFn: async (configuration: string) =>
      notifyUserFromRequest(
        () =>
          govosClient.post(GovosApiRoutes.serviceTemplates(startAppId), {
            name: newTemplateName,
            serviceType: 'authentication',
            configuration
          }),
        {
          successMessage: t('api.saveAsTemplate.successMessage'),
          errorMessage: t('api.saveAsTemplate.errorMessage')
        }
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        predicate: invalidateServiceTemplatesCache()
      });
    }
  });

  const { mutate: updateAuthentication, isPending } = useMutation({
    mutationFn: async (payload: AuthUpdatePayload) =>
      notifyUserFromRequest(
        () =>
          isTemplateMode
            ? govosClient.put(
                GovosApiRoutes.serviceTemplate(startAppId, templateId),
                {
                  name: isTemplatePage ? templateNameInModal : undefined,
                  configuration: JSON.stringify(payload)
                }
              )
            : govosClient.put<GovOsResult<unknown>>(
                GovosApiRoutes.authenticationService(startAppId, processId!),
                payload
              ),
        {
          successMessage: t('EditAuthenticationOptionsModal.successMessage'),
          errorMessage: t('EditAuthenticationOptionsModal.errorMessage'),
          loadingMessage: t('EditAuthenticationOptionsModal.loadingMessage')
        }
      ),
    onSuccess: async (_, payload) => {
      if (isTemplateMode) {
        await queryClient.invalidateQueries({
          predicate: invalidateServiceTemplatesCache()
        });
      } else {
        await queryClient.invalidateQueries({
          predicate: invalidateConfiguredOnlinediensteCache(processId ?? '')
        });
        if (shouldSaveAsTemplate && newTemplateName?.trim()) {
          createTemplate(JSON.stringify(payload));
        }
      }
      onClose();
    }
  });

  const handleSave = handleSubmit((data) => {
    if (shouldSaveAsTemplate && !isTemplateMode) {
      if (!validateTemplateName(newTemplateName)) {
        return;
      }
    }
    if (isTemplatePage && isTemplateMode) {
      if (!validateTemplateName(templateNameInModal)) {
        return;
      }
    }
    const payload: AuthUpdatePayload = {
      loginAsCitizen: data.loginAsCitizen,
      loginAsEmployee: data.loginAsEmployee,
      authLevel: data.authLevel,
      privacyText: data.shouldUseStandardPrivacyText
        ? ''
        : data.customPrivacyText
    };
    updateAuthentication(payload);
  });

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Modal.Header id="modal-header">
        <Text size="xl" strong>
          {t('EditAuthenticationOptionsModal.title')}
        </Text>
      </Modal.Header>
      <Modal.Content>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '24px'
          }}
        >
          <Text size="m" strong>
            {t('EditAuthenticationOptionsModal.description')}
          </Text>

          {/* Servicekonten */}
          <FormRow
            label={t('EditAuthenticationOptionsModal.sections.servicekonten')}
            validationMessage={
              formState.errors.loginAsCitizen?.message ??
              formState.errors.loginAsEmployee?.message
            }
          >
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
            >
              <Controller
                name="loginAsCitizen"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    checked={field.value}
                    onChange={(checked) => field.onChange(checked)}
                    label={t(
                      'EditAuthenticationOptionsModal.fields.buergerkonto'
                    )}
                    subline={t(
                      'EditAuthenticationOptionsModal.fields.buergerkontoSubline'
                    )}
                  />
                )}
              />
              <Controller
                name="loginAsEmployee"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    checked={field.value}
                    onChange={(checked) => field.onChange(checked)}
                    label={t(
                      'EditAuthenticationOptionsModal.fields.organisationskonto'
                    )}
                    subline={t(
                      'EditAuthenticationOptionsModal.fields.organisationskontoSubline'
                    )}
                  />
                )}
              />
            </div>
          </FormRow>

          {/* Vertrauensniveau */}
          <FormRow
            label={t(
              'EditAuthenticationOptionsModal.sections.vertrauensniveau'
            )}
            validationMessage={formState.errors.authLevel?.message as string}
          >
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
            >
              <Accordion
                label={t(
                  'EditAuthenticationOptionsModal.sections.vertrauensniveauInfo'
                )}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px'
                  }}
                >
                  <Text size="s">
                    {t(
                      'EditAuthenticationOptionsModal.vertrauensniveauInfo.storkLevel'
                    )}
                  </Text>
                  <Text size="s" strong>
                    {t(
                      'EditAuthenticationOptionsModal.vertrauensniveauInfo.overviewTitle'
                    )}
                  </Text>
                  <Text size="s">
                    {`- ${t('EditAuthenticationOptionsModal.vertrauensniveauInfo.level1')}`}
                  </Text>
                  <Text size="s">
                    {`- ${t('EditAuthenticationOptionsModal.vertrauensniveauInfo.level3')}`}
                  </Text>
                  <Text size="s">
                    {`- ${t('EditAuthenticationOptionsModal.vertrauensniveauInfo.level4')}`}
                  </Text>
                  <Text size="s" strong>
                    {t(
                      'EditAuthenticationOptionsModal.vertrauensniveauInfo.tableTitle'
                    )}
                  </Text>
                  <div style={{ width: '100%', overflowX: 'auto' }}>
                    <Table style={{ width: '100%', tableLayout: 'fixed' }}>
                      <Table.Columns>
                        <Table.Column
                          value="konto"
                          style={{
                            whiteSpace: 'normal',
                            wordWrap: 'break-word'
                          }}
                        >
                          {t(
                            'EditAuthenticationOptionsModal.vertrauensniveauInfo.table.header.konto'
                          )}
                        </Table.Column>
                        <Table.Column
                          value="niedrig"
                          style={{
                            whiteSpace: 'normal',
                            wordWrap: 'break-word'
                          }}
                        >
                          {t(
                            'EditAuthenticationOptionsModal.vertrauensniveauInfo.table.header.niedrig'
                          )}
                        </Table.Column>
                        <Table.Column
                          value="substanziell"
                          style={{
                            whiteSpace: 'normal',
                            wordWrap: 'break-word'
                          }}
                        >
                          {t(
                            'EditAuthenticationOptionsModal.vertrauensniveauInfo.table.header.substanziell'
                          )}
                        </Table.Column>
                        <Table.Column
                          value="hoch"
                          style={{
                            whiteSpace: 'normal',
                            wordWrap: 'break-word'
                          }}
                        >
                          {t(
                            'EditAuthenticationOptionsModal.vertrauensniveauInfo.table.header.hoch'
                          )}
                        </Table.Column>
                      </Table.Columns>
                      <Table.Rows>
                        <Table.Row>
                          <Table.Cell
                            style={{
                              whiteSpace: 'normal',
                              wordWrap: 'break-word'
                            }}
                          >
                            {t(
                              'EditAuthenticationOptionsModal.vertrauensniveauInfo.table.bundId'
                            )}
                          </Table.Cell>
                          <Table.Cell
                            style={{
                              whiteSpace: 'normal',
                              wordWrap: 'break-word'
                            }}
                          >
                            {t(
                              'EditAuthenticationOptionsModal.vertrauensniveauInfo.table.usernamePassword'
                            )}
                          </Table.Cell>
                          <Table.Cell
                            style={{
                              whiteSpace: 'normal',
                              wordWrap: 'break-word'
                            }}
                          >
                            {t(
                              'EditAuthenticationOptionsModal.vertrauensniveauInfo.table.elsterCertificate'
                            )}
                          </Table.Cell>
                          <Table.Cell
                            style={{
                              whiteSpace: 'normal',
                              wordWrap: 'break-word'
                            }}
                          >
                            {t(
                              'EditAuthenticationOptionsModal.vertrauensniveauInfo.table.onlineId'
                            )}
                          </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                          <Table.Cell
                            style={{
                              whiteSpace: 'normal',
                              wordWrap: 'break-word'
                            }}
                          >
                            {t(
                              'EditAuthenticationOptionsModal.vertrauensniveauInfo.table.bayernId'
                            )}
                          </Table.Cell>
                          <Table.Cell
                            style={{
                              whiteSpace: 'normal',
                              wordWrap: 'break-word'
                            }}
                          >
                            {t(
                              'EditAuthenticationOptionsModal.vertrauensniveauInfo.table.usernamePassword'
                            )}
                          </Table.Cell>
                          <Table.Cell
                            style={{
                              whiteSpace: 'normal',
                              wordWrap: 'break-word'
                            }}
                          >
                            {t(
                              'EditAuthenticationOptionsModal.vertrauensniveauInfo.table.elsterCertificate'
                            )}
                          </Table.Cell>
                          <Table.Cell
                            style={{
                              whiteSpace: 'normal',
                              wordWrap: 'break-word'
                            }}
                          >
                            {t(
                              'EditAuthenticationOptionsModal.vertrauensniveauInfo.table.onlineId'
                            )}
                          </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                          <Table.Cell
                            style={{
                              whiteSpace: 'normal',
                              wordWrap: 'break-word'
                            }}
                          >
                            {t(
                              'EditAuthenticationOptionsModal.vertrauensniveauInfo.table.muk'
                            )}
                          </Table.Cell>
                          <Table.Cell
                            style={{
                              whiteSpace: 'normal',
                              wordWrap: 'break-word'
                            }}
                          >
                            {t(
                              'EditAuthenticationOptionsModal.vertrauensniveauInfo.table.notSupported'
                            )}
                          </Table.Cell>
                          <Table.Cell
                            style={{
                              whiteSpace: 'normal',
                              wordWrap: 'break-word'
                            }}
                          >
                            {t(
                              'EditAuthenticationOptionsModal.vertrauensniveauInfo.table.elsterCertificate'
                            )}
                          </Table.Cell>
                          <Table.Cell
                            style={{
                              whiteSpace: 'normal',
                              wordWrap: 'break-word'
                            }}
                          >
                            {t(
                              'EditAuthenticationOptionsModal.vertrauensniveauInfo.table.onlineId'
                            )}
                          </Table.Cell>
                        </Table.Row>
                      </Table.Rows>
                    </Table>
                  </div>
                </div>
              </Accordion>
              <Radio.RadioGroup
                name="authLevel"
                selectedItem={authLevel}
                onChange={(e) => {
                  const target = e.target as HTMLInputElement;
                  setValue('authLevel', target.value as GovOSAuthLevel, {
                    shouldValidate: true,
                    shouldDirty: true
                  });
                }}
              >
                <Radio
                  label={t(
                    'EditAuthenticationOptionsModal.authLevel.unverified'
                  )}
                  value="unverified"
                  checked={authLevel === 'unverified'}
                  subline={t(
                    'EditAuthenticationOptionsModal.authLevel.unverifiedSubline'
                  )}
                />
                <Radio
                  label={t('EditAuthenticationOptionsModal.authLevel.EMail')}
                  value="EMail"
                  checked={authLevel === 'EMail'}
                  subline={t(
                    'EditAuthenticationOptionsModal.authLevel.EMailSubline'
                  )}
                />
                <Radio
                  label={t(
                    'EditAuthenticationOptionsModal.authLevel.substantial'
                  )}
                  value="substantial"
                  checked={authLevel === 'substantial'}
                  subline={t(
                    'EditAuthenticationOptionsModal.authLevel.substantialSubline'
                  )}
                />
                <Radio
                  label={t(
                    'EditAuthenticationOptionsModal.authLevel.eIDonSend'
                  )}
                  value="eIDonSend"
                  checked={authLevel === 'eIDonSend'}
                  subline={t(
                    'EditAuthenticationOptionsModal.authLevel.eIDonSendSubline'
                  )}
                />
              </Radio.RadioGroup>
            </div>
          </FormRow>

          {/* Datenschutzerklärung Text */}
          <FormRow
            label={t('EditAuthenticationOptionsModal.sections.datenschutz')}
          >
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
            >
              <Accordion
                label={t(
                  'EditAuthenticationOptionsModal.sections.datenschutzInfo'
                )}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px'
                  }}
                >
                  <Text size="s">
                    {t(
                      'EditAuthenticationOptionsModal.datenschutzInfo.explanation'
                    )}
                  </Text>
                  <Text size="s">
                    {t(
                      'EditAuthenticationOptionsModal.datenschutzInfo.linksExplanation'
                    )}
                  </Text>
                  <Text size="s">
                    {`- ${t('EditAuthenticationOptionsModal.datenschutzInfo.placeholders')}`}
                  </Text>
                  <Text size="s">
                    {`- ${t('EditAuthenticationOptionsModal.datenschutzInfo.placeholderNutzungsbedingungen')}`}
                  </Text>
                  <Text size="s" strong>
                    {t(
                      'EditAuthenticationOptionsModal.datenschutzInfo.structure'
                    )}
                  </Text>
                  <Text size="s">
                    {`- ${t('EditAuthenticationOptionsModal.datenschutzInfo.structureItem1')}`}
                  </Text>
                  <Text size="s">
                    {`- ${t('EditAuthenticationOptionsModal.datenschutzInfo.structureItem2')}`}
                  </Text>
                  <Text size="s">
                    {`- ${t('EditAuthenticationOptionsModal.datenschutzInfo.structureItem3')}`}
                  </Text>
                </div>
              </Accordion>
              <Controller
                name="shouldUseStandardPrivacyText"
                control={control}
                render={({ field }) => (
                  <Radio.RadioGroup
                    name="shouldUseStandardPrivacyText"
                    selectedItem={field.value ? 'standard' : 'custom'}
                    onChange={(e) => {
                      const target = e.target as HTMLInputElement;
                      const isStandard = target.value === 'standard';
                      field.onChange(isStandard);
                      if (isStandard) {
                        setValue('customPrivacyText', '');
                      }
                    }}
                  >
                    <Radio
                      label={t(
                        'EditAuthenticationOptionsModal.fields.standardPrivacyText'
                      )}
                      value="standard"
                      checked={field.value}
                    />
                    <Text size="m">
                      {t(
                        'EditAuthenticationOptionsModal.fields.standardPrivacyTextSubline'
                      )}
                    </Text>
                    <Radio
                      label={t(
                        'EditAuthenticationOptionsModal.fields.customPrivacyText'
                      )}
                      value="custom"
                      checked={!field.value}
                      subline={t(
                        'EditAuthenticationOptionsModal.fields.customPrivacyTextSubline'
                      )}
                    />
                  </Radio.RadioGroup>
                )}
              />

              <Controller
                name="customPrivacyText"
                control={control}
                render={({ field }) => (
                  <TextArea
                    {...field}
                    rows={4}
                    style={{ width: '100%' }}
                    disabled={watch('shouldUseStandardPrivacyText')}
                  />
                )}
              />
            </div>
          </FormRow>
          {isTemplatePage && isTemplateMode && (
            <FormRow
              label={t('EditAuthenticationOptionsModal.templateName.label')}
              validationMessage={templateNameError}
              variant={templateNameError ? 'error' : undefined}
            >
              <Input
                value={templateNameInModal}
                onChange={(e) => {
                  setTemplateNameInModal(e.target.value);
                  clearTemplateNameError();
                }}
                placeholder={t(
                  'EditAuthenticationOptionsModal.templateName.placeholder'
                )}
                style={{ width: '100%' }}
              />
            </FormRow>
          )}

          {!isTemplatePage && !isTemplateMode && (
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
            >
              <Checkbox
                checked={shouldSaveAsTemplate}
                onChange={(e) =>
                  setShouldSaveAsTemplate(e.currentTarget.checked)
                }
                label={t(
                  'EditAuthenticationOptionsModal.saveAsTemplate.checkbox'
                )}
              />
              {shouldSaveAsTemplate && (
                <FormRow
                  label={t(
                    'EditAuthenticationOptionsModal.saveAsTemplate.nameLabel'
                  )}
                  validationMessage={templateNameError}
                  variant={templateNameError ? 'error' : undefined}
                >
                  <Input
                    value={newTemplateName}
                    onChange={(e) => {
                      setNewTemplateName(e.target.value);
                      clearTemplateNameError();
                    }}
                    placeholder={t(
                      'EditAuthenticationOptionsModal.saveAsTemplate.namePlaceholder'
                    )}
                    style={{ width: '100%' }}
                  />
                </FormRow>
              )}
            </div>
          )}
        </div>
      </Modal.Content>
      <Modal.Footer>
        <div style={{ display: 'flex', gap: '12px', width: '100%' }}>
          <Button
            variant="secondary-outline"
            onClick={onClose}
            disabled={isPending}
            style={{ flex: 1 }}
          >
            {t('EditAuthenticationOptionsModal.actions.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={() => void handleSave()}
            disabled={
              isPending ||
              (!shouldUseStandardPrivacyText && !customPrivacyText.trim())
            }
            style={{ flex: 1 }}
          >
            {t('EditAuthenticationOptionsModal.actions.save')}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
